import React, { useMemo } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';

import { PAYMENT_TYPE_MAP } from '@/config';
import { parseFen2Yuan, parseFen2Currency } from '@/utils/number';
import handoverBusinessTotalImg from '@/assets/img/handover_business_total.png';
import handoverIncomeRefundImg from '@/assets/img/handover_income_refund.png';
import handoverIncomeTotalimg from '@/assets/img/handover_income_total.png';
import styles from './index.module.scss';

// 账务项目
export const PROJECT_ITEM_MAP = {
  night_fee: intl.get('handover_project_name_night_fee'),
  hour_room_fee: intl.get('handover_project_name_hour_room_fee'),
  over_time_fee: intl.get('handover_project_name_over_time_fee'),
  manual_room_fee: intl.get('handover_project_name_manual_room_fee'),
  goods_fee: intl.get('handover_project_name_goods_fee'),
  compensation: intl.get('handover_project_name_compensation'),
  other_fee: intl.get('handover_project_name_other_fee'),
  day_rate: intl.get('handover_project_name_day_rate'),
  time_rate: intl.get('handover_project_name_time_rate'),
  overtime_rate: intl.get('handover_project_name_overtime_rate'),
  craft_rate: intl.get('handover_project_name_craft_rate'),
  sale_goods: intl.get('handover_project_name_sale_goods'),
  damages: intl.get('handover_project_name_damages'),
  room_other_expense: intl.get('handover_project_name_room_other_expense'),
  reception_rate: intl.get('handover_project_name_reception_rate'),
  reception_sale_goods: intl.get('handover_project_name_reception_sale_goods'),
  reception_damages: intl.get('handover_project_name_reception_damages'),
  reception_other_expense: intl.get('handover_project_name_reception_other_expense'),
  reception_refund_sum: intl.get('handover_project_name_reception_refund_sum'),
};
export const PROJECT_REMARK_MAP = {
  night_fee: intl.get('handover_project_remark_night_fee'),
  hour_room_fee: '',
  over_time_fee: intl.get('handover_project_remark_over_time_fee'),
  manual_room_fee: intl.get('handover_project_remark_manual_room_fee'),
  goods_fee: intl.get('handover_project_remark_goods_fee'),
  compensation: intl.get('handover_project_remark_compensation'),
  other_fee: intl.get('handover_project_remark_other_fee'),

  day_rate: intl.get('handover_project_remark_day_rate'),
  time_rate: intl.get('handover_project_remark_time_rate'),
  overtime_rate: intl.get('handover_project_remark_overtime_rate'),
  craft_rate: intl.get('handover_project_remark_craft_rate'),
  sale_goods: intl.get('handover_project_remark_sale_goods'),
  damages: intl.get('handover_project_remark_damages'),
  room_other_expense: intl.get('handover_project_remark_room_other_expense'),
  reception_rate: intl.get('handover_project_remark_reception_rate'),
  reception_sale_goods: intl.get('handover_project_remark_reception_sale_goods'),
  reception_damages: intl.get('handover_project_remark_reception_damages'),
  reception_other_expense: intl.get('handover_project_remark_reception_other_expense'),
  reception_refund_sum: intl.get('handover_project_remark_reception_refund_sum'),
};

export const BILL_PROJECT_REMARK_MAP = {
  Room: PROJECT_REMARK_MAP, // 房间业务
  Reception: {
    // 前台业务
    ...PROJECT_REMARK_MAP,
    reception_refund_sum: intl.get('handover_project_remark_bill_reception_refund_sum'),
  },
};

const businessColumns: ColumnsType<any> = [
  {
    title: intl.get('handover_table_column_project'),
    dataIndex: 'projectCode',
    render: (val) => PROJECT_ITEM_MAP[val] || '',
    width: 160,
  },
  {
    title: intl.get('handover_bill_income_column_income'),
    dataIndex: 'income',
    render: (val) => parseFen2Currency(val),
    width: 120,
  },
  {
    title: intl.get('handover_table_column_remark'),
    render: (_, record) => PROJECT_REMARK_MAP[record?.projectCode] || '',
  },
];

const incomeRefundColumns: ColumnsType<any> = [
  {
    title: intl.get('handover_table_column_project'),
    dataIndex: 'paymentType',
    render: (val) => PAYMENT_TYPE_MAP[val] || '',
  },
  {
    title: intl.get('handover_table_column_income'),
    dataIndex: 'income',
    render: (val) => parseFen2Currency(val),
  },
  {
    title: intl.get('handover_table_column_refund'),
    dataIndex: 'refund',
    render: (val) => parseFen2Currency(val),
  },
  {
    title: intl.get('handover_table_column_total'),
    dataIndex: 'total',
    render: (val) => parseFen2Currency(val),
  },
];

const calcTotal = (list, key) => {
  return list?.reduce((total, currentItem) => {
    let tempTotal = total;
    tempTotal += (currentItem || {})[key];
    return tempTotal;
  }, 0);
};

const BaseBusiness: React.FC<any> = ({ baseBusinessLoading, incomeList, paymentTypeList }) => {
  const projectIncomeTotal = useMemo(() => {
    return calcTotal(incomeList, 'income');
  }, [incomeList]);

  const { channelIncomeTotal, channelRefundTotal, channelIncomeRefundTotal } = useMemo(() => {
    const incomeTotal = calcTotal(paymentTypeList, 'income');
    const refundTotal = calcTotal(paymentTypeList, 'refund');
    const incomeRefundTotal = calcTotal(paymentTypeList, 'total');
    return {
      channelIncomeTotal: incomeTotal,
      channelRefundTotal: refundTotal,
      channelIncomeRefundTotal: incomeRefundTotal,
    };
  }, [paymentTypeList]);

  return (
    <>
      <div>
        <div className={styles.cardContainer}>
          <div className={styles.handoverCardItem}>
            <img
              className={styles.base1}
              src={handoverBusinessTotalImg}
              alt={intl.get('handover_room_indicator_income')}
            />
            <div>
              <div className={styles.title}>{intl.get('handover_room_indicator_income')}</div>
              <div className={styles.money}>{parseFen2Yuan(projectIncomeTotal)}</div>
            </div>
          </div>
          <div className={styles.handoverCardItem}>
            <img
              className={styles.base2}
              src={handoverIncomeRefundImg}
              alt={intl.get('handover_room_indicator_refund')}
            />
            <div>
              <div className={styles.title}>{intl.get('handover_room_indicator_refund')}</div>
              <div className={styles.money}>{parseFen2Yuan(channelIncomeRefundTotal)}</div>
            </div>
          </div>
          <div className={styles.handoverCardItem}>
            <img
              className={styles.base3}
              src={handoverIncomeTotalimg}
              alt={intl.get('handover_room_indicator_total')}
            />
            <div>
              <div className={styles.title}>{intl.get('handover_room_indicator_total')}</div>
              <div className={styles.money}>
                {parseFen2Yuan(channelIncomeRefundTotal - projectIncomeTotal)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ margin: '24px 0 16px' }}>
        <div className="groupLabel">{intl.get('handover_bill_income_block_title')}</div>
      </div>
      <Table
        rowKey="projectCode"
        columns={businessColumns}
        loading={baseBusinessLoading}
        dataSource={incomeList}
        pagination={false}
        sticky
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                {intl.get('handover_table_summary_total')}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} colSpan={2}>
                {parseFen2Currency(projectIncomeTotal)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      <div style={{ margin: '24px 0 16px' }}>
        <div className="groupLabel">{intl.get('handover_bill_channel_block_title')}</div>
      </div>
      <Table
        rowKey="paymentType"
        columns={incomeRefundColumns}
        loading={baseBusinessLoading}
        dataSource={paymentTypeList}
        pagination={false}
        sticky
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                {intl.get('handover_table_summary_total')}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                {parseFen2Currency(channelIncomeTotal)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                {parseFen2Currency(channelRefundTotal)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                {parseFen2Currency(channelIncomeRefundTotal)}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </>
  );
};

export default BaseBusiness;
